import { useEffect, useState, memo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";

import { request } from "@/Api";
import { formatDateStringToDayMonthYear, formattedCurrency } from "@/utils";
import {
    StatusBadge,
    WidgetHeaderRow,
    ListRowDivider,
    ListRowItem,
    SeeAllRow,
} from "../primitives";

const getBillStatus = (bill) => {
    if (bill.certainty === "Actual") return { label: "Paid", color: "green" };
    if (bill.sent) return { label: "Sent", color: "grey" };
    return null;
};

const UpcomingBills = memo(() => {
    const [bills, setBills] = useState(null);

    useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        const params = {
            offset: 0,
            limit: 10,
            orders: "+bill_date",
            bill_date: `gte:${today}`,
        };
        request
            .get("/bills", { params })
            .then((r) => setBills(r.data.items || []))
            .catch(() => setBills([]));
    }, []);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
            }}
        >
            <WidgetHeaderRow
                title="Upcoming Bills"
                count={bills?.length ?? null}
            />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                    overflow: "auto",
                }}
            >
                {!bills &&
                    [1, 2, 3, 4].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={44}
                            sx={{
                                borderRadius: "6px",
                                bgcolor: "action.hover",
                            }}
                        />
                    ))}
                {bills?.map((bill, i) => {
                    const status = getBillStatus(bill);
                    return (
                        <Box
                            key={bill.id || i}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                            }}
                        >
                            {i > 0 && <ListRowDivider />}
                            <ListRowItem
                                primary={formattedCurrency(
                                    bill.actual_amount || bill.estimated_amount,
                                    bill.currency || "CAD",
                                )}
                                sub1={
                                    bill.bill_date
                                        ? formatDateStringToDayMonthYear(
                                              bill.bill_date,
                                          )
                                        : ""
                                }
                                sub2={bill.project_name || ""}
                                right={
                                    status ? (
                                        <StatusBadge
                                            label={status.label}
                                            color={status.color}
                                        />
                                    ) : null
                                }
                            />
                        </Box>
                    );
                })}
                {bills?.length === 0 && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            fontFamily: "Inter Tight, sans-serif",
                            px: "8px",
                        }}
                    >
                        No upcoming bills
                    </Typography>
                )}
            </Box>

            <SeeAllRow href="/bills" label="All bills" />
        </Box>
    );
});

UpcomingBills.displayName = "UpcomingBills";

export default UpcomingBills;
